import React, {useMemo, useState} from 'react';
import {forgotPassword} from '@jarvis/swagger';
import Image from 'next/image';

import {AuthForm} from '@src/components/forms/AuthForm';
import PasswordImageSrc from '@public/img/illustrations/password-form.svg';
import TextField from '@src/components/atoms/TextField';
import Button from '@src/components/atoms/Button';
import FormErrorMessage from '@src/components/atoms/FormErrorMessage';
import ResetPasswordEmailSentCard from '@src/components/molecules/ResetPasswordEmailSentCard';
import {useBoolean} from '@src/hooks/useBoolean';
import {validateEmail} from '@src/utils/validations';

import * as S from './ForgotPasswordForm.styles';

interface ForgotPasswordFormProps {
  initialError?: string;
}

const ForgotPasswordForm = ({initialError}: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState<string>('');
  const [emailSent, setEmailSent] = useBoolean(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | undefined>(initialError);

  const emailValidation = useMemo(() => validateEmail(email), [email]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setForgotPasswordError(undefined);

    try {
      await forgotPassword({email});
      setEmailSent.setTrue();
    } catch (error) {
      setForgotPasswordError('Something went wrong');
    }
  };

  const resetForm = () => {
    setEmail('');
    setEmailSent.setFalse();
    setForgotPasswordError(undefined);
  };

  if (emailSent) {
    return <ResetPasswordEmailSentCard email={email} resetForm={resetForm} />;
  }

  return (
    <S.Wrapper>
      <Image src={PasswordImageSrc} alt={'Left key and right lock'} />

      <S.Title>Forgot Password</S.Title>

      {forgotPasswordError && <FormErrorMessage message={forgotPasswordError} />}

      <S.InfoText>Please enter your registered email to receive password reset instructions</S.InfoText>

      <AuthForm onSubmit={handleSubmit}>
        <TextField
          label="Email"
          name="email"
          type="email"
          inputMode="email"
          placeholder="Email address"
          required
          value={email}
          error={emailValidation?.error}
          isValid={emailValidation?.isValid}
          onChange={handleEmailChange}
        />

        <Button type="submit" fullWidth disabled={!emailValidation?.isValid}>
          Reset Password
        </Button>
      </AuthForm>
    </S.Wrapper>
  );
};

export default ForgotPasswordForm;
